import React, { useEffect, useRef, useState } from "react";
import SearchAthletes from "./SearchAthletes";
import './Search.css';
import toastr from "toastr";
import { MagnifyingGlassIcon } from "../../icons/heroIcons";

const TOP_PLAYS_SPORT = "topplays";

const normalizeSportKey = (value) => String(value || "").trim().toLowerCase();

const getSearchSourceLabel = (sportKey) => (
  normalizeSportKey(sportKey) === TOP_PLAYS_SPORT
    ? "Top Picks"
    : String(sportKey || "").trim().toUpperCase()
);

const tagSearchResults = (athletes, sourceSport) => {
  const sourceKey = normalizeSportKey(sourceSport);
  const sourceLabel = getSearchSourceLabel(sourceKey);

  return Array.isArray(athletes)
    ? athletes.map((athlete) => ({
      ...athlete,
      _search_source_key: sourceKey,
      _search_source_label: sourceLabel,
    }))
    : [];
};

const getGlobalFallbackSports = (currentSport, availableSports) => {
  if (!Array.isArray(availableSports)) {
    return [];
  }

  const currentSportKey = normalizeSportKey(currentSport);
  const seenSports = new Set();

  return availableSports.reduce((sports, sportKey) => {
    const normalizedSport = normalizeSportKey(sportKey);

    if (
      !normalizedSport ||
      normalizedSport === TOP_PLAYS_SPORT ||
      normalizedSport === currentSportKey ||
      seenSports.has(normalizedSport)
    ) {
      return sports;
    }

    seenSports.add(normalizedSport);
    sports.push(normalizedSport);
    return sports;
  }, []);
};

const getSearchResultKey = (athlete) => [
  normalizeSportKey(athlete?._search_source_key),
  normalizeSportKey(athlete?.league),
  String(athlete?.athlete_id ?? athlete?.uid ?? athlete?.term_id ?? ""),
  String(athlete?.game_id ?? ""),
  String(athlete?.stat_key ?? ""),
  String(athlete?.promo ?? ""),
  String(athlete?.easy ?? ""),
  String(athlete?.hard ?? ""),
  String(athlete?.name ?? "").trim().toLowerCase(),
].join("|");

const dedupeSearchResults = (athletes) => {
  const seen = new Set();

  return athletes.filter((athlete) => {
    const key = getSearchResultKey(athlete);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export default function SearchInput({sport, sports, stat, setLoading, setAthletes, setAthletesLoading, getAthletes, cancelBoardRequest, filterAthleteList, isOpen = false, resetSignal = 0, onSearchModeChange, onSearchQueryChange}) {
  const [searchInput, setSearchInput] = useState("");
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const searchControllerRef = useRef(null);
  const filterAthleteListRef = useRef(filterAthleteList);
  const cancelBoardRequestRef = useRef(cancelBoardRequest);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      searchControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    filterAthleteListRef.current = filterAthleteList;
  }, [filterAthleteList]);

  useEffect(() => {
    cancelBoardRequestRef.current = cancelBoardRequest;
  }, [cancelBoardRequest]);

  useEffect(() => {
    requestIdRef.current += 1;
    setSearchInput("");
  }, [resetSignal]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) {
      return;
    }

    const focusId = window.setTimeout(() => {
      inputRef.current.focus();
    }, 180);

    return () => {
      window.clearTimeout(focusId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof onSearchModeChange === "function") {
      onSearchModeChange(Boolean(searchInput.trim()));
    }

    if (typeof onSearchQueryChange === "function") {
      onSearchQueryChange(searchInput.trim());
    }
  }, [onSearchModeChange, onSearchQueryChange, searchInput]);

  useEffect(() => {
    const trimmedInput = searchInput.trim();

    if (trimmedInput.length < 2) {
      return undefined;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    searchControllerRef.current?.abort();
    const searchController = new AbortController();
    searchControllerRef.current = searchController;
    if (cancelBoardRequestRef.current) {
      cancelBoardRequestRef.current();
    }
    const timeoutId = setTimeout(async () => {
      setAthletesLoading(true);

      const response = await SearchAthletes(sport, trimmedInput, { signal: searchController.signal });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (response === null) {
        toastr["error"]("Search is temporarily unavailable. Please try again.", "Error");
        setAthletesLoading(false);
        return;
      }

      setLoading(false);
      const currentSportKey = normalizeSportKey(sport);
      let filtered = filterAthleteListRef.current(tagSearchResults(response, sport));
      const shouldSearchGlobally = currentSportKey === TOP_PLAYS_SPORT || filtered.length === 0;

      if (shouldSearchGlobally) {
        const fallbackSports = getGlobalFallbackSports(sport, sports);

        if (fallbackSports.length > 0) {
          const fallbackResponses = [];
          let fallbackIndex = 0;
          const searchNextFallback = async () => {
            while (fallbackIndex < fallbackSports.length && !searchController.signal.aborted) {
              const fallbackSport = fallbackSports[fallbackIndex];
              fallbackIndex += 1;
              fallbackResponses.push({
                athletes: await SearchAthletes(fallbackSport, trimmedInput, { signal: searchController.signal }),
                sport: fallbackSport,
              });
            }
          };
          await Promise.all([searchNextFallback(), searchNextFallback()]);

          if (requestId !== requestIdRef.current) {
            return;
          }

          const globalResults = fallbackResponses
            .filter((fallbackResponse) => Array.isArray(fallbackResponse.athletes))
            .flatMap((fallbackResponse) => tagSearchResults(fallbackResponse.athletes, fallbackResponse.sport));
          filtered = filterAthleteListRef.current(dedupeSearchResults([
            ...filtered,
            ...globalResults,
          ]));
        }
      }

      setAthletes(filtered);
      setAthletesLoading(false);

      if (filtered.length === 0) {
        toastr["warning"]("No athletes found matching search.", "Notice");
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      searchController.abort();
    };
  }, [searchInput, setAthletes, setAthletesLoading, setLoading, sport, sports]);

  const restoreCurrentBoard = () => {
    requestIdRef.current += 1;
    searchControllerRef.current?.abort();
    setAthletes([]);
    setAthletesLoading(true);
    getAthletes(sport, stat);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);

    if (value.trim() === "") {
      restoreCurrentBoard();
    } else if (value.trim().length < 2) {
      setAthletesLoading(false);
    } else {
      setAthletesLoading(true);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    restoreCurrentBoard();
  };

  return (
    <div
      aria-hidden={!isOpen}
      className={`col-md-5 col-sm-12 mb-3 pl-0 pr-3 board-search-shell${isOpen ? " is-open" : ""}`}
      id="board-search-shell"
    >
      <label htmlFor="search-input" className="sr-only">Search Athletes</label>
      <span className="board-search">
        <MagnifyingGlassIcon className="board-search-icon" aria-hidden="true" />
        <input
          className="board-search-input"
          id="search-input"
          placeholder="Search"
          ref={inputRef}
          tabIndex={isOpen ? 0 : -1}
          type="text"
          value={searchInput}
          autoComplete="off"
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchInput && (
          <button type="button" className="clear-button" onClick={clearSearch} aria-label="Clear search">
            &times;
          </button>
        )}
      </span>
    </div>
  )
}
